import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  Scatter,
  BarChart,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { List, Eye, EyeOff } from 'lucide-react';
import type { ChartType, ChartStudy, AxisOptions, ChartColor, UniversalChart } from '@shared/index';
import { CHART_COLORS, STUDY_COLORS, applyStudiesToData, getChartColors, getColorAsString as getStudyColorAsString } from '../../lib/chartUtils';
import { useTooltipFormatting, useChartDisplayPreferences, useCustomPalettes } from '../../contexts/ChartPreferencesContext';
import { 
  createTooltipFormatter, 
  createLabelFormatter,
  formatTooltipValueSemantically,
  type YAxisType
} from '../../lib/tooltipFormatter';
import { getColorForCurrentTheme } from '../../lib/themeAwareColors';
import { useTheme } from '../../lib/themeUtils';

// Standardized data point interface for the universal renderer
export interface StandardizedDataPoint {
  name: string;
  value: number;
  index: number;
  [key: string]: any; // Allow additional properties
}

// Legacy configuration interface for backward compatibility
export interface UniversalChartConfig {
  data: StandardizedDataPoint[];
  chartType: ChartType;
  studies?: ChartStudy[];
  axisOptions?: AxisOptions | null;
  displayOptions?: { showGridLines?: boolean; showLegend?: boolean } | null;
  height?: number;
  colors?: string[];
  datasetLabel?: string;
  isTimeBasedData?: boolean;
  enableAdvancedFeatures?: boolean; // For time formatting, intervals, etc.
  metadata?: {
    yAxisType?: YAxisType;
  };
}

interface UniversalChartRendererProps {
  // New simplified interface - just pass the chart!
  chart?: UniversalChart;
  // Legacy interface for backward compatibility
  config?: UniversalChartConfig;
  // Optional overrides
  height?: number;
  className?: string;
  enableAdvancedFeatures?: boolean;
}

const UniversalChartRenderer: React.FC<UniversalChartRendererProps> = ({
  chart,
  config,
  height: heightOverride,
  className = "",
  enableAdvancedFeatures: enableAdvancedFeaturesOverride,
}) => {
  // Inject CSS styles for chart legend scrollbar
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .chart-legend-scrollable::-webkit-scrollbar {
        width: 6px;
      }
      .chart-legend-scrollable::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 3px;
      }
      .chart-legend-scrollable::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.3);
        border-radius: 3px;
      }
      .chart-legend-scrollable::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0, 0, 0, 0.5);
      }
      .chart-legend-scrollable {
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Legend toggle state - use chart displayOptions if available, otherwise default to true
  const [showLegend, setShowLegend] = useState(true);
  const finalShowLegend = chart?.displayOptions?.showLegend !== undefined ? chart.displayOptions.showLegend : showLegend;
  
  // Handle both new chart prop and legacy config prop
  const effectiveConfig = chart ? {
    // Convert chart data - preserve multi-series structure if present
    data: (chart as any).isMultiSeries 
      ? chart.data as StandardizedDataPoint[]  // Use data as-is for multi-series
      : chart.data.map((point, index) => ({    // Convert to single-series format
          name: String(point[chart.xAxis] || ''),
          value: Number(point[chart.yAxis] || 0),
          index,
          ...point // Include all original properties
        })) as StandardizedDataPoint[],
    chartType: chart.chartType,
    studies: chart.studies || [],
    axisOptions: chart.axisOptions,
    displayOptions: chart.displayOptions,
    height: heightOverride || 300,
    // Use series colors if available, otherwise get colors from palette, or fallback to single color
    colors: (chart as any).seriesInfo?.colors || 
            chart.colors || 
            ((chart as any).colorPalette ? getChartColors((chart as any).colorPalette, []) : undefined) ||
            (chart.configuration?.colorPalette ? getChartColors(chart.configuration.colorPalette, []) : undefined) ||
            (chart.configuration?.singleColor ? [chart.configuration.singleColor] : undefined),
    datasetLabel: chart.title || 'Data',
    isTimeBasedData: false, // Can be enhanced later with metadata detection
    enableAdvancedFeatures: enableAdvancedFeaturesOverride || false,
    metadata: chart.metadata,
  } : config!;

  const {
    data,
    chartType,
    studies = [],
    axisOptions,
    displayOptions,
    height = 300,
    colors,
    datasetLabel = 'Data',
    isTimeBasedData = false,
    enableAdvancedFeatures = false,
    metadata,
  } = effectiveConfig;

  const isDarkMode = useTheme();
  
  // Calculate dynamic height - use provided height or calculate based on container
  const dynamicHeight = height || Math.max(300, window.innerHeight * 0.4);

  // Resolve theme-aware colors to simple strings before passing to Recharts
  const resolvedChartColors = (colors && colors.length > 0 ? colors : CHART_COLORS).map(c => getColorForCurrentTheme(c, isDarkMode));
  
  console.log('[UniversalChartRenderer] Color resolution:', {
    'chart.colors': chart?.colors,
    'chart.configuration?.singleColor': chart?.configuration?.singleColor,
    'effectiveConfig.colors': colors,
    'resolvedChartColors': resolvedChartColors
  });

  // console.log('[Renderer] Entry: chartType:', chartType, 'isTimeBasedData:', isTimeBasedData, 'enableAdvancedFeatures:', enableAdvancedFeatures);
  // console.log('[Renderer] Received config.axisOptions:', axisOptions ? JSON.parse(JSON.stringify(axisOptions)) : axisOptions);
  // console.log('[Renderer] Received config.data (first 3):', data?.slice(0, 3));
  // console.log('[Renderer] Using chartColors (first 5 resolved):', resolvedChartColors.slice(0,5));
  
  const tooltipFormatting = useTooltipFormatting();
  const chartDisplayPrefs = useChartDisplayPreferences();
  const customPalettes = useCustomPalettes();
  
  const tooltipValueFormatter = (value: any) => {
    if (typeof value === 'number') {
      return formatTooltipValueSemantically(value, metadata?.yAxisType);
    }
    return value;
  };

  const labelFormatter = createLabelFormatter(tooltipFormatting);

  if (!data || data.length === 0) {
    return <div className={`flex items-center justify-center h-full text-muted-foreground ${className}`}><p>No data available</p></div>;
  }

  // Detect if this is multi-series data (has properties other than name, value, index)
  const samplePoint = data[0]
  const hasMultiSeries = samplePoint && Object.keys(samplePoint).some(key => 
    key !== 'name' && key !== 'value' && key !== 'index' && 
    typeof samplePoint[key] === 'number'
  )
  
  // Get all data series keys (excluding name, value, index)
  const seriesKeys = hasMultiSeries ? 
    Object.keys(samplePoint).filter(key => 
      key !== 'name' && key !== 'value' && key !== 'index' && 
      typeof samplePoint[key] === 'number'
    ) : []
  
  // Use resolvedChartColors, or fallback to user default palette if resolvedChartColors is empty
  let finalChartColors = resolvedChartColors.length > 0 
    ? resolvedChartColors 
    : getChartColors(chartDisplayPrefs.defaultColorPalette, customPalettes).map(c => getColorForCurrentTheme(c, isDarkMode));

  // For multi-series charts, ensure we have enough colors by cycling through the palette
  if (hasMultiSeries && seriesKeys.length > 0) {
    // If we have fewer colors than series, cycle through the available colors
    if (finalChartColors.length < seriesKeys.length) {
      const expandedColors = [];
      for (let i = 0; i < seriesKeys.length; i++) {
        expandedColors.push(finalChartColors[i % finalChartColors.length]);
      }
      finalChartColors = expandedColors;
    }
    
    // Only fall back to default colors if we have no colors at all
    if (finalChartColors.length === 0) {
      const defaultMultiColors = ['#FF8635', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16'];
      finalChartColors = defaultMultiColors;
    }
  }

  // Prepare data (apply studies & ensure timeValue numeric field)
  let processedData: StandardizedDataPoint[] = data;
  if (studies.length > 0 && studies.some(s => s.visible)) {
    console.log('[UniversalChartRenderer] Applying studies:', studies.filter(s => s.visible).map(s => ({ type: s.type, period: s.period, visible: s.visible })));
    processedData = applyStudiesToData([...data], studies, 'name', 'value') as StandardizedDataPoint[];
    console.log('[UniversalChartRenderer] After applying studies, first data point:', processedData[0]);
    console.log('[UniversalChartRenderer] Available data keys:', Object.keys(processedData[0] || {}));
  } else {
    processedData = [...data];
  }

  // If time-based, attach numeric timestamp for proper linear axis and sort chronologically
  if (isTimeBasedData) {
    processedData = processedData
      .map((d) => ({ ...d, timeValue: new Date(d.name).getTime() }))
      .filter((d) => !isNaN(d.timeValue))
      .sort((a, b) => a.timeValue - b.timeValue);
  }

  const showGridLines = displayOptions?.showGridLines !== false;
  // --- Smarter time-axis label formatting ---
  const firstDate = isTimeBasedData ? new Date(processedData[0]?.name) : null;
  const lastDate = isTimeBasedData ? new Date(processedData[processedData.length - 1]?.name) : null;
  const datasetSpanMs = (firstDate && lastDate && !isNaN(firstDate.getTime()) && !isNaN(lastDate.getTime())) ? (lastDate.getTime() - firstDate.getTime()) : 0;

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  const formatTimeLabel = (value: any) => {
    if (!isTimeBasedData || !enableAdvancedFeatures) return value;

    const date = new Date(value);
    if (isNaN(date.getTime())) return value;

    // If span < 1 day, show HH:MM
    if (datasetSpanMs < ONE_DAY_MS) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }

    // If span < 7 days, show Mon DD HH:MM
    if (datasetSpanMs < 7 * ONE_DAY_MS) {
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    // If span < 1 year, show Mon DD
    if (datasetSpanMs < 365 * ONE_DAY_MS) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Otherwise include year
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  let interval = 0;
  if (enableAdvancedFeatures) {
    const dataLength = data.length;
    if (dataLength > 50) interval = Math.floor(dataLength / 12);
    else if (dataLength > 30) interval = Math.floor(dataLength / 10);
    else if (dataLength > 15) interval = Math.floor(dataLength / 8);
    else if (dataLength > 7) interval = Math.floor(dataLength / 5);
    else interval = 0; // Show all labels for small datasets
  }

  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const isMediumScreenOrWider = screenWidth >= 768;
  const xAxisHeight = isTimeBasedData && enableAdvancedFeatures ? (isMediumScreenOrWider ? 65 : 50) : 45;

  const xMinOption = axisOptions?.xMin;
  const xMaxOption = axisOptions?.xMax;
  const yMinOption = axisOptions?.yMin;
  const yMaxOption = axisOptions?.yMax;

  const xDomain: [any, any] = [xMinOption ?? 'auto', xMaxOption ?? 'auto'];
  const yDomain: [any, any] = [yMinOption ?? 'auto', yMaxOption ?? 'auto'];
  
  let xAxisRechartsType: 'category' | 'number' = 'category';
  if ((xMinOption !== null && xMinOption !== undefined) || (xMaxOption !== null && xMaxOption !== undefined)) {
    if (typeof xMinOption === 'number' || typeof xMaxOption === 'number') {
      xAxisRechartsType = 'number';
    } else if (isTimeBasedData && (typeof xMinOption === 'string' || typeof xMaxOption === 'string')) {
      // If X data are date strings and min/max are also date strings, treat as category for Recharts XAxis with dataKey="name"
      // unless we convert all 'name' data to timestamps and use scale="time"
      xAxisRechartsType = 'category'; 
    }
  }

  const renderStudyLines = () => {
    return studies.map((study) => {
      if (!study.visible) return null;
      
      let dataKey = '';
      switch (study.type) {
        case 'movingAverage':
          dataKey = `ma_${study.period}`; // Fixed: match chartUtils.tsx format
          break;
        case 'regression':
          dataKey = 'regression';
          break;
        case 'polynomialTrend':
          dataKey = `poly_${study.degree}`; // Fixed: match chartUtils.tsx format
          break;
        case 'exponentialSmoothing':
          dataKey = `exp_${study.alpha}`; // Fixed: match chartUtils.tsx format
          break;
        // Add other study types here as needed
        default:
          return null;
      }
      
      const studyColor = getStudyColorAsString(study.color, isDarkMode);
      console.log(`[UniversalChartRenderer] Rendering study ${study.name} with color:`, study.color, '-> resolved:', studyColor);

      return (
        <Line
          key={study.id}
          type="monotone"
          dataKey={dataKey}
          stroke={studyColor}
          strokeWidth={2}
          dot={false}
          name={study.name}
          strokeDasharray="5 5" // Differentiate study lines
        />
      );
    });
  };

  const renderStatisticalMarkers = () => { /* ... */ };

  if (chartType === 'pie' || chartType === 'doughnut') {
    return (
      <div className="relative w-full h-full">
        <ResponsiveContainer width="100%" height={dynamicHeight} className={className}>
          <RechartsPieChart>
            <Pie data={processedData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={Math.min(dynamicHeight * 0.35, 140)} innerRadius={chartType === 'doughnut' ? Math.min(dynamicHeight * 0.2, 80) : 0} fill="#8884d8" dataKey="value" nameKey="name">
              {processedData.map((entry, index) => (<Cell key={`cell-${index}`} fill={(entry as any).color || finalChartColors[index % finalChartColors.length]} />))}
            </Pie>
            <Tooltip formatter={tooltipValueFormatter} labelFormatter={labelFormatter}/>
          </RechartsPieChart>
        </ResponsiveContainer>
      </div>
    );
  } else {
    const ChartComponent = enableAdvancedFeatures && (chartType === 'bar' && studies.length === 0) ? BarChart : ComposedChart;

    return (
      <div className="relative w-full h-full">
        <ResponsiveContainer width="100%" height={dynamicHeight} className={className}>
          <ChartComponent 
            data={processedData} 
            margin={{ 
              top: 5, 
              right: 20, 
              left: 20, 
              bottom: (isTimeBasedData && enableAdvancedFeatures && xAxisHeight > 50) ? xAxisHeight - 40 : (finalShowLegend && hasMultiSeries ? 80 : 5)
            }}
          >
            {showGridLines && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis
              dataKey={isTimeBasedData ? 'timeValue' : 'name'}
              scale={isTimeBasedData ? 'time' : undefined}
              type={isTimeBasedData ? 'number' : xAxisRechartsType}
              domain={isTimeBasedData ? ['auto', 'auto'] : xDomain}
              tickFormatter={isTimeBasedData ? formatTimeLabel : undefined}
              tick={{ fontSize: 10 }}
              angle={isTimeBasedData && enableAdvancedFeatures ? -45 : 0}
              textAnchor={isTimeBasedData && enableAdvancedFeatures ? 'end' : 'middle'}
              height={xAxisHeight}
              interval={enableAdvancedFeatures ? interval : 0}
              allowDuplicatedCategory={!isTimeBasedData && xAxisRechartsType === 'category' ? true : undefined}
            />
            <YAxis 
              tick={{ fontSize: 11 }} 
              allowDecimals={processedData.some(d => Number.isFinite(d.value) && !Number.isInteger(d.value))} 
              domain={yDomain} 
            />
            <Tooltip formatter={tooltipValueFormatter} labelFormatter={enableAdvancedFeatures && isTimeBasedData ? formatTimeLabel : labelFormatter} />
            {hasMultiSeries && finalShowLegend && (
              <Legend 
                wrapperStyle={{ 
                  paddingLeft: '20px', 
                  paddingTop: '10px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }} 
                content={(props) => {
                  if (!props.payload) return null;
                  
                  return (
                    <div 
                      className="chart-legend-scrollable"
                      style={{ 
                        maxHeight: '60px', 
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingRight: '8px'
                      }}
                    >
                      <ul 
                        style={{ 
                          padding: 0, 
                          margin: 0, 
                          textAlign: 'left',
                          listStyle: 'none',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}
                      >
                        {props.payload.map((entry, index) => (
                          <li
                            key={`legend-${index}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '12px',
                              color: isDarkMode ? '#D1D5DB' : '#374151',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <div
                              style={{
                                width: '12px',
                                height: '12px',
                                backgroundColor: entry.color,
                                marginRight: '6px',
                                borderRadius: '2px',
                                flexShrink: 0
                              }}
                            />
                            <span style={{ color: isDarkMode ? '#D1D5DB' : '#374151' }}>
                              {entry.value}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }}
              />
            )}
            
            {/* Render single series or multi-series based on data structure */}
            {hasMultiSeries ? (
              // Multi-series rendering
              seriesKeys.map((seriesKey, index) => {
                const color = finalChartColors[index % finalChartColors.length] || '#ef4444'
                if (chartType === 'bar') {
                  return <Bar key={seriesKey} dataKey={seriesKey} fill={color} name={seriesKey} />
                } else if (chartType === 'line') {
                  return <Line key={seriesKey} type="monotone" dataKey={seriesKey} stroke={color} strokeWidth={2} dot={false} name={seriesKey} />
                } else if (chartType === 'area') {
                  return <Area key={seriesKey} type="monotone" dataKey={seriesKey} stroke={color} fill={color} fillOpacity={0.6} name={seriesKey} />
                } else if (chartType === 'scatter') {
                  return <Scatter key={seriesKey} dataKey={seriesKey} fill={color} name={seriesKey} />
                }
                return null
              })
            ) : (
              // Single series rendering
              <>
                {chartType === 'bar' && <Bar dataKey="value" fill={finalChartColors[0] || '#ef4444'} name={datasetLabel}/>}
                {chartType === 'line' && <Line type="monotone" dataKey="value" stroke={finalChartColors[0] || '#ef4444'} strokeWidth={2} dot={false} name={datasetLabel}/>}
                {chartType === 'area' && <Area type="monotone" dataKey="value" stroke={finalChartColors[0] || '#ef4444'} fill={finalChartColors[0] || '#ef4444'} fillOpacity={0.6} name={datasetLabel}/>}
                {chartType === 'scatter' && <Scatter dataKey="value" fill={finalChartColors[0] || '#ef4444'} name={datasetLabel}/>}
              </>
            )}
            
            {renderStudyLines()}
            
            {/* {renderStatisticalMarkers()} */}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    );
  }
};

export default UniversalChartRenderer;